import { useRef, useState, useMemo } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { X } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { Switch } from '@/shared/ui/switch'
import { Label } from '@/shared/ui/label'
import { Input } from '@/shared/ui/input'
import { SearchableSelect } from '@/shared/ui/searchable-select'
import { ComplianceBadge } from '@/shared/ui/compliance-badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog'
import {
  RestoreDialog,
  type RestorePreviewUnit,
  type RestoreSelection,
} from '@/shared/ui/restore-dialog'
import { t } from '@/shared/lib/i18n'
import type { ComplianceFilter } from '@/shared/lib/compliance'
import {
  departmentPath,
  departmentsPath,
  deletePreviewDepartmentPath,
  editDepartmentPath,
  newDepartmentPath,
  restorePreviewDepartmentPath,
  restoreDepartmentPath,
  departmentSignoffChainPath,
  organizationPath,
  unitsPath,
} from '@/shared/lib/routes'
import { RETURN_TO_DEPARTMENTS } from '@/shared/lib/signoff-chain-return-to'
import { visitWithParams } from '@/shared/lib/visit-with-params'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { useCan } from '@/shared/hooks/useCan'
import { useDebouncedCallback } from '@/shared/hooks/useDebouncedCallback'
import { Resources, Actions } from '@/shared/rbac'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'
import type { WebDepartment } from '@/shared/types/web'
import type { SharedProps } from '@/shared/types/SharedProps'
import OrganizationTabs from '../OrganizationTabs'

interface SiteOption {
  value: number
  label: string
}

interface Props {
  departments: WebDepartment[]
  pagination: PaginationMeta
  sites: SiteOption[]
  searchQuery?: string
  filters: { siteId: number | null; showDeleted: boolean; compliance: ComplianceFilter }
  [key: string]: unknown
}

interface DepartmentDeletePreview {
  units_count: number
  instruments_count: number
}

const DepartmentsIndexPage = () => {
  const {
    departments,
    pagination,
    sites,
    filters,
    searchQuery: initialSearchQuery = '',
  } = usePage<Props>().props
  const { complianceSettings } = usePage<SharedProps>().props

  // Search, Site, Compliance filter and Show disabled all round-trip through the server and keep
  // each other's params, so they combine and apply to every page (REBUILD-534).
  const visitDepartments = (updates: Record<string, string | null>) =>
    visitWithParams(departmentsPath(), updates)
  const toggleShowDeleted = (next: boolean) => visitDepartments({ show_deleted: next ? '1' : null })

  useBreadcrumbs([
    { label: t('nav.administration'), href: organizationPath() },
    { label: t('nav.departmentsAndUnits'), href: departmentsPath() },
    { label: t('nav.departments') },
  ])

  const searchInputRef = useRef<HTMLInputElement>(null)
  const [search, setSearch] = useState(initialSearchQuery)

  const debouncedSearch = useDebouncedCallback((value: string) => {
    visitDepartments({ q: value.trim() || null })
  }, 300)

  const clearSearch = () => {
    setSearch('')
    debouncedSearch.cancel()
    visitDepartments({ q: null })
    searchInputRef.current?.focus()
  }

  const [deletingDepartment, setDeletingDepartment] = useState<WebDepartment | null>(null)
  const [deletePreview, setDeletePreview] = useState<DepartmentDeletePreview | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [restoringDepartment, setRestoringDepartment] = useState<WebDepartment | null>(null)
  const [restorePreview, setRestorePreview] = useState<RestorePreviewUnit[]>([])
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false)
  const [restorePreviewLoading, setRestorePreviewLoading] = useState(false)

  const isAdmin = useCan(Resources.DEPARTMENT, Actions.CREATE)
  const canDelete = useCan(Resources.DEPARTMENT, Actions.DELETE)

  const hasAnySignoffChainAccess = departments.some((dept) => dept.signoffChainEditable)

  const handleRestoreClick = async (dept: WebDepartment) => {
    setRestoringDepartment(dept)
    setRestorePreview([])
    setRestorePreviewLoading(true)
    setRestoreDialogOpen(true)
    try {
      const response = await fetch(restorePreviewDepartmentPath(dept.id), {
        headers: { Accept: 'application/json' },
      })
      if (response.ok) {
        const preview = (await response.json()) as RestorePreviewUnit[]
        setRestorePreview(preview)
      }
    } catch {
      // preview stays empty
    } finally {
      setRestorePreviewLoading(false)
    }
  }

  const handleRestoreConfirm = (selection: RestoreSelection) => {
    if (!restoringDepartment) return
    router.post(restoreDepartmentPath(restoringDepartment.id), {
      unit_ids: selection.unitIds,
      instrument_ids: selection.instrumentIds,
    })
    setRestoringDepartment(null)
  }

  const handleDeleteClick = async (dept: WebDepartment) => {
    setDeletingDepartment(dept)
    try {
      const response = await fetch(deletePreviewDepartmentPath(dept.id), {
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) throw new Error('Preview fetch failed')
      const preview = (await response.json()) as DepartmentDeletePreview
      setDeletePreview(preview)
    } catch {
      setDeletePreview(null)
    }
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (!deletingDepartment) return
    router.delete(departmentPath(deletingDepartment.id), { preserveScroll: true })
    setDeleteDialogOpen(false)
    setDeletingDepartment(null)
    setDeletePreview(null)
  }

  const deleteDialogBody = () => {
    if (!deletePreview) return t('organization.departments.deleteConfirmBodyEmpty')
    const { units_count, instruments_count } = deletePreview
    if (units_count === 0 && instruments_count === 0) {
      return t('organization.departments.deleteConfirmBodyEmpty')
    }
    return t('organization.departments.deleteConfirmBody', {
      unitsCount: units_count,
      instrumentsCount: instruments_count,
    })
  }

  const [complianceSort, setComplianceSort] = useState<'asc' | 'desc' | null>(null)
  const complianceFilter = filters.compliance ?? 'all'

  const activeFilterCount = [
    search.trim().length > 0,
    Boolean(filters.siteId),
    complianceFilter !== 'all',
    filters.showDeleted,
    complianceSort !== null,
  ].filter(Boolean).length

  const clearFilters = () => {
    debouncedSearch.cancel()
    setSearch('')
    setComplianceSort(null)
    router.visit(departmentsPath(), { preserveState: false })
  }

  const filteredDepartments = useMemo(() => {
    if (!complianceSort) return departments
    return [...departments].sort((a, b) => {
      const aVal = a.rollingCompliancePercent ?? -1
      const bVal = b.rollingCompliancePercent ?? -1
      return complianceSort === 'asc' ? aVal - bVal : bVal - aVal
    })
  }, [departments, complianceSort])

  return (
    <div className="space-y-6">
      <OrganizationTabs />
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('organization.departments.title')}</h1>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button asChild>
              <Link href={newDepartmentPath()}>{t('organization.departments.newDepartment')}</Link>
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative w-full sm:max-w-sm">
          <Input
            ref={searchInputRef}
            placeholder={t('organization.departments.searchPlaceholder')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              debouncedSearch(e.target.value)
            }}
            className={search ? 'pr-9' : undefined}
          />
          {search && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-full px-2"
              aria-label={t('organization.departments.searchClearAria')}
              onClick={clearSearch}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <SearchableSelect
          className="w-48"
          value={filters.siteId ? String(filters.siteId) : 'all'}
          onValueChange={(value) => visitDepartments({ site_id: value === 'all' ? null : value })}
          placeholder={t('organization.departments.filterBySite')}
          options={[
            { value: 'all', label: t('organization.departments.allSites') },
            ...sites.map((site) => ({ value: String(site.value), label: site.label })),
          ]}
        />
        <SearchableSelect
          className="w-52"
          value={complianceFilter}
          onValueChange={(value) =>
            visitDepartments({ compliance: value === 'all' ? null : value })
          }
          placeholder={t('organization.departments.filterByCompliance')}
          options={[
            { value: 'all', label: t('organization.departments.allCompliance') },
            { value: 'high', label: t('organization.departments.complianceHigh') },
            { value: 'medium', label: t('organization.departments.complianceMedium') },
            { value: 'low', label: t('organization.departments.complianceLow') },
            { value: 'none', label: t('organization.departments.complianceNone') },
          ]}
        />
        <div className="ml-auto flex items-center gap-2">
          <Switch
            id="departments-show-deleted"
            checked={filters.showDeleted}
            onCheckedChange={toggleShowDeleted}
          />
          <Label htmlFor="departments-show-deleted">
            {t('organization.departments.showDeleted')}
          </Label>
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-2 text-muted-foreground"
              onClick={clearFilters}
            >
              {t('organization.departments.clearFilters')}
            </Button>
          )}
        </div>
      </div>

      {/* Mobile: card list */}
      <div className="space-y-2 lg:hidden">
        {filteredDepartments.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-muted-foreground">{t('organization.departments.empty')}</p>
          </div>
        ) : (
          filteredDepartments.map((dept) => (
            <div
              key={dept.id}
              className={`rounded-lg border bg-card p-3 shadow-sm space-y-2${dept.discarded ? ' opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{dept.name}</span>
                  {dept.discarded && (
                    <Badge variant="secondary">{t('organization.departments.deleted')}</Badge>
                  )}
                </div>
                {!dept.discarded && dept.unitsCount > 0 && (
                  <Link
                    href={`${unitsPath()}?department_id=${dept.id}`}
                    className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted/80"
                  >
                    {dept.unitsCount} {t('organization.departments.table.units')}
                  </Link>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {t('organization.departments.table.id')}: {dept.id}
              </p>
              {dept.siteName && <p className="text-sm text-muted-foreground">{dept.siteName}</p>}
              <div className="text-xs">
                <ComplianceBadge
                  percent={dept.rollingCompliancePercent}
                  windowDays={complianceSettings?.windowDays}
                  colorCodes={complianceSettings?.colorCodes}
                />
              </div>
              {(isAdmin || dept.signoffChainEditable) && (
                <div className="flex justify-end gap-2 pt-1">
                  {dept.discarded ? (
                    isAdmin &&
                    canDelete &&
                    (dept.siteDiscarded ? (
                      <p className="pt-1 text-xs text-destructive">
                        {t('organization.departments.restoreBlockedSiteDeleted')}
                      </p>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void handleRestoreClick(dept)}
                      >
                        {t('organization.departments.restore')}
                      </Button>
                    ))
                  ) : (
                    <>
                      {isAdmin && (
                        <Button asChild variant="outline" size="sm">
                          <Link href={editDepartmentPath(dept.id)}>
                            {t('organization.departments.edit')}
                          </Link>
                        </Button>
                      )}
                      {dept.signoffChainEditable && (
                        <Button asChild variant="outline" size="sm">
                          <Link
                            href={departmentSignoffChainPath(dept.id, {
                              return_to: RETURN_TO_DEPARTMENTS,
                            })}
                          >
                            {t('organization.departments.signoffChain')}
                          </Link>
                        </Button>
                      )}
                      {isAdmin && canDelete && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => void handleDeleteClick(dept)}
                        >
                          {t('organization.departments.delete')}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Desktop: table */}
      <div className="hidden rounded-md border lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">{t('organization.departments.table.id')}</TableHead>
              <TableHead>{t('organization.departments.table.name')}</TableHead>
              <TableHead>{t('organization.departments.table.site')}</TableHead>
              <TableHead>{t('organization.departments.table.units')}</TableHead>
              <TableHead
                className="cursor-pointer select-none whitespace-nowrap"
                onClick={() =>
                  setComplianceSort((prev) =>
                    prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc',
                  )
                }
              >
                {t('organization.departments.table.complianceScore')}
                {complianceSort === 'asc' ? ' ↑' : complianceSort === 'desc' ? ' ↓' : ' ↕'}
              </TableHead>
              {(isAdmin || hasAnySignoffChainAccess) && (
                <TableHead className="w-[1%] text-right">
                  {t('organization.departments.table.actions')}
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredDepartments.map((dept) => (
              <TableRow key={dept.id} className={dept.discarded ? 'opacity-60' : undefined}>
                <TableCell className="text-xs text-muted-foreground">{dept.id}</TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {dept.name}
                    {dept.discarded && (
                      <Badge variant="secondary">{t('organization.departments.deleted')}</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>{dept.siteName ?? '—'}</TableCell>
                <TableCell>
                  {!dept.discarded && dept.unitsCount > 0 ? (
                    <Link
                      href={`${unitsPath()}?department_id=${dept.id}`}
                      className="text-primary hover:underline"
                    >
                      {dept.unitsCount}
                    </Link>
                  ) : (
                    dept.unitsCount
                  )}
                </TableCell>
                <TableCell>
                  <ComplianceBadge
                    percent={dept.rollingCompliancePercent}
                    windowDays={complianceSettings?.windowDays}
                    colorCodes={complianceSettings?.colorCodes}
                  />
                </TableCell>
                {(isAdmin || dept.signoffChainEditable) && (
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {dept.discarded ? (
                        isAdmin &&
                        canDelete &&
                        (dept.siteDiscarded ? (
                          <span
                            className="text-xs text-destructive"
                            title={t('organization.departments.restoreBlockedSiteDeleted')}
                          >
                            {t('organization.departments.restoreBlockedSiteDeleted')}
                          </span>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void handleRestoreClick(dept)}
                          >
                            {t('organization.departments.restore')}
                          </Button>
                        ))
                      ) : (
                        <>
                          {isAdmin && (
                            <Button asChild variant="outline" size="sm">
                              <Link href={editDepartmentPath(dept.id)}>
                                {t('organization.departments.edit')}
                              </Link>
                            </Button>
                          )}
                          {dept.signoffChainEditable && (
                            <Button asChild variant="outline" size="sm">
                              <Link
                                href={departmentSignoffChainPath(dept.id, {
                                  return_to: RETURN_TO_DEPARTMENTS,
                                })}
                              >
                                {t('organization.departments.signoffChain')}
                              </Link>
                            </Button>
                          )}
                          {isAdmin && canDelete && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => void handleDeleteClick(dept)}
                            >
                              {t('organization.departments.delete')}
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <RestoreDialog
        open={restoreDialogOpen}
        onOpenChange={setRestoreDialogOpen}
        resourceName={restoringDepartment?.name ?? ''}
        loading={restorePreviewLoading}
        units={restorePreview}
        onConfirm={handleRestoreConfirm}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('organization.departments.deleteConfirmTitle')}: {deletingDepartment?.name}
            </AlertDialogTitle>
            <AlertDialogDescription>{deleteDialogBody()}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('organization.departments.form.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('organization.departments.deleteConfirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          {pagination.totalPages > 1 &&
            `${t('organization.departments.paginationPage')} ${pagination.page} ${t('organization.departments.paginationOf')} ${pagination.totalPages} (`}
          {pagination.total} {t('organization.departments.paginationNoun')}
          {pagination.totalPages > 1 && ')'}
        </span>
        <div className="flex gap-2">
          {pagination.page > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const params = new URLSearchParams(window.location.search)
                params.set('page', String(pagination.page - 1))
                router.visit(`${departmentsPath()}?${params}`, { preserveState: true })
              }}
            >
              {t('organization.departments.previous')}
            </Button>
          )}
          {pagination.page < pagination.totalPages && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const params = new URLSearchParams(window.location.search)
                params.set('page', String(pagination.page + 1))
                router.visit(`${departmentsPath()}?${params}`, { preserveState: true })
              }}
            >
              {t('organization.departments.next')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default DepartmentsIndexPage
